/**
 * Responsibility: Implement effect check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { AttributeKey, LivingEntityStats } from "@anyquest/shared-core"
import { totalMinFromClock } from '../../temporal'
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

function scanExpiredModifiers(entity: LivingEntityStats, totalMin: number): { expired: number; malformed: number } {
  let expired = 0
  let malformed = 0
  for (const rawKey of Object.keys(entity.modifiers ?? {})) {
    const key = rawKey as AttributeKey
    const mods = entity.modifiers[key] ?? []
    for (const mod of mods) {
      const bad = !Number.isFinite(mod?.val) || !String(mod?.label ?? '').trim() || !String(mod?.source ?? '').trim()
      if (bad) malformed += 1
      if (mod?.expiresAt != null && mod.expiresAt <= totalMin) expired += 1
    }
  }
  return { expired, malformed }
}

export const effectCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []
  const totalMin = totalMinFromClock(state.world.clock)

  let expired = 0
  let malformed = 0
  const playerScan = scanExpiredModifiers(state.player, totalMin)
  expired += playerScan.expired
  malformed += playerScan.malformed

  for (const companion of state.companions ?? []) {
    const scan = scanExpiredModifiers(companion, totalMin)
    expired += scan.expired
    malformed += scan.malformed
  }

  if (expired > 0) {
    incidents.push({
      code: 'effect_expired_modifiers_present',
      domain: 'effect',
      severity: 'low',
      confidence: 0.99,
      message: `${expired} expired modifiers are still active.`,
      remediationPlan: 'Prune expired modifiers and archive to dormant effects.',
      context: { expired, totalMin },
    })
  }

  if (malformed > 0) {
    incidents.push({
      code: 'effect_malformed_modifiers',
      domain: 'effect',
      severity: 'medium',
      confidence: 0.98,
      message: `${malformed} malformed modifiers detected.`,
      remediationPlan: 'Drop malformed modifier entries.',
      context: { malformed },
    })
  }

  const oversizedDormant = (state.player.dormantEffects?.length ?? 0) > 60
    || (state.companions ?? []).some(c => (c.dormantEffects?.length ?? 0) > 60)
  if (oversizedDormant) {
    incidents.push({
      code: 'effect_dormant_effects_oversized',
      domain: 'effect',
      severity: 'low',
      confidence: 0.95,
      message: 'Dormant effects exceed retention cap.',
      remediationPlan: 'Trim dormant effects to bounded retention window.',
    })
  }

  return { passed: incidents.length === 0, incidents }
}
